import { useState, useRef } from 'react';
import api from '@/api';
import {
  Upload, FileText, Loader2, CheckCircle2, AlertTriangle,
  X, Link2
} from 'lucide-react';

export default function ProcoreSyncPanel({ projectId, onImported, onClose }) {
  const [mode, setMode] = useState('csv'); // 'csv' | 'connect'
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);
  const fileRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const res = await api.get(`/closeout/projects/${projectId}/procore/status`);
      setStatus(res.data);
    } catch (err) { console.error(err); }
  };

  useState(() => { fetchStatus(); });

  const handleUploadCSV = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/closeout/projects/${projectId}/procore/import-csv`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
    } catch (err) { console.error(err); }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="procore-sync-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-sm w-[650px] max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground flex items-center gap-2">
              <Link2 className="w-5 h-5 text-primary" /> Procore / External Sync
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">Import closeout documents from Procore or CSV export</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground/80"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {result ? (
            <div className="space-y-4">
              <div className="text-center py-4">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <p className="text-lg font-heading text-foreground">{result.imported} Documents Imported</p>
                {result.skipped > 0 && (
                  <p className="text-sm text-amber-400 mt-1">{result.skipped} rows skipped</p>
                )}
              </div>

              {result.skipped_details?.length > 0 && (
                <div className="bg-card border border-border rounded-sm p-4 max-h-40 overflow-y-auto">
                  <p className="text-[10px] font-heading uppercase tracking-widest text-amber-500/80 mb-2">Skipped Rows</p>
                  {result.skipped_details.map((s, i) => (
                    <div key={i} className="text-xs text-muted-foreground py-1 border-b border-border/50 last:border-0">
                      <span className="text-amber-400">{s.reason}</span>
                      {s.row?.document_title && <span className="ml-2 text-muted-foreground">— {s.row.document_title}</span>}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => { setFile(null); setResult(null); }}
                  className="px-4 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
                  Import More
                </button>
                <button onClick={() => { onImported(); onClose(); }} data-testid="procore-done-btn"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-heading uppercase tracking-wider rounded-sm transition-colors">
                  Done
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Mode tabs */}
              <div className="flex gap-2 mb-5">
                <button onClick={() => setMode('csv')}
                  className={`px-3 py-1.5 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors ${
                    mode === 'csv' ? 'bg-primary/15 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground/80'
                  }`}>
                  CSV Import
                </button>
                <button onClick={() => setMode('connect')}
                  className={`px-3 py-1.5 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors ${
                    mode === 'connect' ? 'bg-primary/15 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground/80'
                  }`}>
                  Direct Connect
                </button>
              </div>

              {mode === 'csv' ? (
                <div className="space-y-4">
                  <div className="bg-card border border-border rounded-sm p-4">
                    <p className="text-xs text-muted-foreground mb-2">Export a CSV from Procore (Documents tab → Export) or prepare one with these columns:</p>
                    <code className="text-[10px] font-mono text-primary bg-orange-950/20 px-2 py-1 rounded-sm block">
                      document_title, trade_description, csi_number, vendor_name, document_type, status, url
                    </code>
                    <p className="text-[10px] text-muted-foreground mt-2">Flexible headers accepted: title, trade, csi, vendor, type, status, url</p>
                  </div>

                  <div onClick={() => fileRef.current?.click()}
                    className={`flex flex-col items-center justify-center h-28 border-2 border-dashed rounded-sm cursor-pointer transition-colors ${
                      file ? 'border-primary/40 bg-orange-950/10' : 'border-border hover:border-slate-600'
                    }`}>
                    {file ? (
                      <>
                        <FileText className="w-6 h-6 text-primary mb-1" />
                        <p className="text-sm text-foreground/80">{file.name}</p>
                      </>
                    ) : (
                      <>
                        <Upload className="w-6 h-6 text-muted-foreground mb-1" />
                        <p className="text-sm text-muted-foreground">Select CSV file</p>
                      </>
                    )}
                    <input ref={fileRef} type="file" className="hidden" accept=".csv"
                      onChange={e => setFile(e.target.files[0])} />
                  </div>

                  <div className="flex justify-end">
                    <button onClick={handleUploadCSV} disabled={!file || uploading}
                      data-testid="procore-csv-submit"
                      className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-heading uppercase tracking-wider rounded-sm transition-colors">
                      {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      Import CSV
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Link2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-sm text-muted-foreground mb-2">Direct Procore Connection</p>
                  <p className="text-xs text-muted-foreground mb-6">
                    Connect your Procore account to automatically sync closeout documents.
                    Requires Procore OAuth2 credentials (Client ID + Secret).
                  </p>
                  <div className="bg-amber-950/20 border border-amber-900 rounded-sm p-4 text-xs text-amber-400 max-w-sm mx-auto">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    Coming soon — use CSV import for now. When you're ready to connect,
                    we'll need your Procore App credentials from <span className="font-mono">developers.procore.com</span>.
                  </div>

                  {status && status.status !== 'not_configured' && (
                    <div className="mt-4 p-3 bg-card border border-border rounded-sm text-xs text-muted-foreground">
                      Last sync: {status.last_sync ? new Date(status.last_sync).toLocaleString() : 'Never'} | Docs synced: {status.documents_synced || 0}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
